/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react'
import { Tree } from 'antd'
import type { DataNode } from 'antd/es/tree'
import { useHistory, useLocation } from 'react-router-dom'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'
import { TeamTreeNode } from './hooks/useTeamsTree'
import { paragraphXSmall } from '../css'

interface TeamsTreeProps {
  data: TeamTreeNode[]
}

const ORG_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

const treeContainerStyle = css`
  padding: 0 16px;
  overflow: hidden;

  // https://sportninja.atlassian.net/browse/SN-7031
  // Denys asked to return the scrollbar. If we want to remove this, just uncomment the lines below
  /* Hide scrollbar for container and all children */
  // scrollbar-width: none; /* Firefox */
  // -ms-overflow-style: none; /* IE and Edge */
  // &::-webkit-scrollbar {
  //   display: none; /* Chrome, Safari, Opera */
  // }
  // * {
  //   scrollbar-width: none;
  //   -ms-overflow-style: none;
  //   &::-webkit-scrollbar {
  //     display: none;
  //   }
  // }

  .ant-tree {
    background: transparent;
    color: ${colors.NEUTRAL_0};
  }

  .ant-tree-list-holder-inner {
    width: 100% !important;
  }

  .ant-tree-title {
    display: block;
    overflow: hidden;
    width: 100%;
  }

  // https://sportninja.atlassian.net/browse/SN-7031
  // Denys asked to return the scrollbar. If we want to remove this, just uncomment the lines below
  // .ant-tree-list-scrollbar {
  //   display: none !important;
  // }

  .ant-tree-switcher {
    display: none;
    align-items: center;
    justify-content: center;
    width: 16px;
    color: ${colors.NEUTRAL_50};
    // remove the effect when hover
    &:hover {
      background: transparent !important;
    }
  }

  .ant-tree-switcher-leaf-line {
    display: none;
  }

  .ant-tree-node-content-wrapper {
    background: transparent !important;
    padding: 0;
    display: flex;
    align-items: center;
    min-height: 24px;
    overflow: hidden;
    flex: 1;
    min-width: 0;

    &:hover {
      background: transparent !important;
    }
  }

  .ant-tree-treenode {
    padding: 0;
    align-items: center;
    width: 100%;
    overflow: hidden;

    &:hover {
      background: transparent;
    }
  }

  .ant-tree-indent-unit {
    width: 8px;
  }
`

const treeNodeStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px;
  cursor: pointer;
  border-radius: 8px;
  width: 100%;
  overflow: hidden;
  min-width: 0;

  &:hover {
    background: ${colors.SECONDARY_400};
  }
`

const nodeImageStyle = css`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  background-color: ${colors.NEUTRAL_0};
`

const nodeTextContainerStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`

const nodeNameStyle = (isSelected: boolean) => css`
  ${paragraphXSmall}
  font-weight: 500;
  color: ${isSelected ? colors.PRIMARY : colors.NEUTRAL_0};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const nodeRoleStyle = css`
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-weight: 400;
  line-height: 15px;
  color: ${colors.NEUTRAL_50};
  line-height: 1;
`

interface TreeNodeItemProps {
  node: TeamTreeNode
  onClick: () => void
  isSelected: boolean
}

const TreeNodeItem: React.FC<TreeNodeItemProps> = ({
  node,
  onClick,
  isSelected,
}) => {
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.onerror = null
    e.currentTarget.src = ORG_PLACEHOLDER
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClick()
  }

  return (
    <div css={treeNodeStyle} onClick={handleClick}>
      <img
        src={getImageThumbnailId(node) || node?.image?.url || ORG_PLACEHOLDER}
        alt={node.name}
        css={nodeImageStyle}
        onError={handleImageError}
        loading='lazy'
      />
      <div css={nodeTextContainerStyle}>
        <p css={nodeNameStyle(isSelected)}>{node.name}</p>
        {node.membership?.role_name && (
          <p css={nodeRoleStyle}>{node.membership.role_name}</p>
        )}
        {node && node?.roles && node?.roles?.length > 0 && (
          <p css={nodeRoleStyle}>
            {Array.from(new Set(node.roles.map((role) => role.role_name))).join(
              ', '
            )}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * TeamsTree - Displays a hierarchical tree of user teams
 * Uses Ant Design Tree with custom node rendering
 */
const TeamsTree: React.FC<TeamsTreeProps> = ({ data }) => {
  const history = useHistory()
  const location = useLocation()

  // Extract team ID from path like /team/{teamId} or /team/{teamId}/statistics
  const currentTeamId = useMemo(() => {
    const match = location.pathname.match(/^\/team\/([^/]+)/)
    return match ? match[1] : null
  }, [location.pathname])

  const handleNodeClick = React.useCallback(
    (nodeId: string) => {
      history.push(`${ROUTES.TEAM_ROOT}/${nodeId}`)
    },
    [history]
  )

  const transformToTreeData = React.useCallback(
    (nodes: TeamTreeNode[]): DataNode[] => {
      return nodes.map((node) => ({
        key: node.id,
        title: (
          <TreeNodeItem
            node={node}
            onClick={() => handleNodeClick(node.id)}
            isSelected={node.id === currentTeamId}
          />
        ),
        children: node.children
          ? transformToTreeData(node.children)
          : undefined,
        isLeaf: node.children_count === 0,
      }))
    },
    [handleNodeClick, currentTeamId]
  )

  const treeData = useMemo(
    () => transformToTreeData(data),
    [data, transformToTreeData]
  )

  return (
    <div css={treeContainerStyle}>
      <Tree
        treeData={treeData}
        defaultExpandAll={false}
        showIcon={false}
        selectable={false}
        blockNode
        height={350}
        motion={false}
      />
    </div>
  )
}

export default TeamsTree
